import type { CSSProperties, JSX } from 'react';
import { TextField } from '../../../../_components/TextField.js';
import type { Palette } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';
import { ChoiceRow } from './ChoiceRow.js';

interface SingleChoiceFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: string) => void;
}

const OTHER_PREFIX = 'other:';

// Single-choice with optional "Other" row that reveals a text input when
// selected. Value is stored as `other:<typed-text>` so the survey author
// can distinguish a free-text response from a preset option without an
// extra schema field. Empty `other:` is allowed while the user is typing.
export function SingleChoiceField({ palette, question, value, onChange }: SingleChoiceFieldProps): JSX.Element {
  const options = question.settings?.options ?? [];
  const allowOther = Boolean(question.settings?.allowOther);

  const valueString = typeof value === 'string' ? value : '';
  const isOther = valueString.startsWith(OTHER_PREFIX);
  const otherText = isOther ? valueString.slice(OTHER_PREFIX.length) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {options.map((option) => (
        <ChoiceRow
          key={option.value}
          option={option}
          selected={valueString === option.value}
          onClick={() => onChange(option.value)}
          palette={palette}
          multi={false}
        />
      ))}
      {allowOther && (
        <>
          <ChoiceRow
            option={{ label: 'Other…', value: OTHER_PREFIX }}
            selected={isOther}
            onClick={() => onChange(OTHER_PREFIX)}
            palette={palette}
            multi={false}
          />
          {isOther && (
            <div style={otherInputWrap()}>
              <TextField
                palette={palette}
                value={otherText}
                onChange={(next) => onChange(`${OTHER_PREFIX}${next}`)}
                placeholder="Tell us more"
                ariaLabel="Other"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function otherInputWrap(): CSSProperties {
  return { padding: '4px 30px 8px' };
}
